import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Twitter, Share2, Copy, Check } from "lucide-react";
import AdBanner, { ADS_ENABLED } from "../components/AdBanner";

function ArticleMeta({ art }) {
  useEffect(() => {
    if (!art) return;
    const siteName = "MediaCrypto";
    const title = art.title + " | " + siteName;
    const description = art.excerpt || art.content.slice(0, 160).replace(/\n/g, " ") + "...";
    const image = art.image_url || "https://mediacrypto.ai/og-image.png";
    const url = "https://mediacrypto.ai/news/" + (art.slug || art.id);
    document.title = title;
    const setMeta = (selector, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const attr = selector.includes("[property") ? "property" : "name";
        const key = selector.match(/["']([^"']+)["']/)[1];
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta('meta[name="description"]', description);
    setMeta('meta[name="title"]', title);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:image"]', image);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:type"]', "article");
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', image);
    setMeta('meta[name="twitter:card"]', "summary_large_image");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    let schema = document.getElementById("article-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "article-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "headline": art.title,
      "description": description,
      "image": [image],
      "datePublished": art.created_at,
      "dateModified": art.updated_at || art.created_at,
      "author": { "@type": "Person", "name": art.author_name || "MediaCrypto", "url": "https://mediacrypto.ai/about" },
      "publisher": { "@type": "Organization", "name": "MediaCrypto", "logo": { "@type": "ImageObject", "url": "https://mediacrypto.ai/favicon.svg" } },
      "mainEntityOfPage": { "@type": "WebPage", "@id": url }
    });
    return () => {
      document.title = "MediaCrypto — Real-Time Crypto News, Prices & Analysis";
      setMeta('meta[name="description"]', "MediaCrypto delivers real-time cryptocurrency news, live Bitcoin and Ethereum prices, market analysis, and community discussion. Stay ahead of the crypto market.");
      setMeta('meta[property="og:title"]', "MediaCrypto — Real-Time Crypto News, Prices & Analysis");
      setMeta('meta[property="og:description"]', "MediaCrypto delivers real-time cryptocurrency news, live Bitcoin and Ethereum prices, market analysis, and community discussion.");
      setMeta('meta[property="og:type"]', "website");
      setMeta('meta[property="og:url"]', "https://mediacrypto.ai/");
      canonical.setAttribute("href", "https://mediacrypto.ai/");
      if (schema) schema.remove();
    };
  }, [art]);
  return null;
}

function renderContent(content) {
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s]+)/g;

  return content.split("\n\n").map((para, i) => {
    if (!para.trim()) return null;

    const elements = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = linkPattern.exec(para)) !== null) {
      if (match.index > lastIndex) {
        elements.push(para.slice(lastIndex, match.index));
      }

      const isMarkdownLink = match[1] !== undefined;
      const linkText = isMarkdownLink ? match[1] : match[3];
      const linkUrl = isMarkdownLink ? match[2] : match[3];
      const isInternal = linkUrl.includes("mediacrypto.ai");

      elements.push(
        React.createElement(
          "a",
          {
            key: key++,
            href: linkUrl,
            target: isInternal ? "_self" : "_blank",
            rel: "noopener noreferrer",
            className: "text-yellow-400 hover:underline",
          },
          linkText
        )
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < para.length) {
      elements.push(para.slice(lastIndex));
    }

    return React.createElement(
      "p",
      { key: i, className: "text-[var(--text-secondary)] text-lg leading-8" },
      elements
    );
  });
}

function ShareButtons({ title, url }) {
  const [copied, setCopied] = useState(false);

  const shareTwitter = () => {
    const text = encodeURIComponent(title + " via @MediaCryptoAI");
    const link = encodeURIComponent(url);
    window.open("https://twitter.com/intent/tweet?text=" + text + "&url=" + link, "_blank", "noopener,noreferrer");
  };

  const shareReddit = () => {
    const link = encodeURIComponent(url);
    const text = encodeURIComponent(title);
    window.open("https://reddit.com/submit?url=" + link + "&title=" + text, "_blank", "noopener,noreferrer");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-10 pt-6 border-t border-[var(--border-soft)]">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] flex items-center gap-1">
          <Share2 className="w-3 h-3" /> Share
        </span>
        <button onClick={shareTwitter} className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] text-[var(--text-secondary)] hover:border-yellow-400 hover:text-yellow-400 transition text-xs font-mono uppercase tracking-wide">
          <Twitter className="w-3 h-3" /> Twitter
        </button>
        <button onClick={shareReddit} className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] text-[var(--text-secondary)] hover:border-orange-400 hover:text-orange-400 transition text-xs font-mono uppercase tracking-wide">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
          </svg>
          Reddit
        </button>
        <button onClick={copyLink} className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] text-[var(--text-secondary)] hover:border-green-400 hover:text-green-400 transition text-xs font-mono uppercase tracking-wide">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied!" : "Copy Link"}
        </button>
      </div>
    </div>
  );
}

export default function NewsDetail() {
  const { slug } = useParams();
  const [art, setArt] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/news/slug/" + slug);
        setArt(data);
        if (data.category_slug) {
          const r = await api.get("/news?category=" + data.category_slug + "&limit=4");
            setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 3));
        }
      } catch (err) {
        const detail = err?.response?.data?.detail || "";
        if (detail.startsWith("MOVED:")) {
          const realSlug = detail.replace("MOVED:", "");
          window.location.replace("/news/" + realSlug);
          return;
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (loading || art) return;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [loading, art]);

  if (loading) return (
    <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Loading article...</div>
  );
  if (!art) return (
    <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Article not found.</div>
  );

  const articleUrl = "https://mediacrypto.ai/news/" + (art.slug || art.id);

  return (
    <div className="pt-0 pb-20" data-testid="news-detail">
      <ArticleMeta art={art} />

      {art.image_url && (
        <div className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
          <img src={art.image_url} alt={art.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/20" />
        </div>
      )}

      <article className={"max-w-3xl mx-auto px-6 " + (art.image_url ? "-mt-40 relative z-10" : "mt-10")}>

        {art.category_slug && (
          <Link to={"/categories/" + art.category_slug} className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-[0.2em] bg-yellow-400 text-black hover:bg-yellow-500 transition">
            {art.category_slug.replace(/-/g, " ")}
          </Link>
        )}

        <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter mt-4 leading-[1.05]" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.8), 0 1px 3px rgba(0,0,0,1)" }}>
          {art.title}
        </h1>

                <div className="mt-5 flex items-center gap-3 text-sm font-mono text-zinc-400 flex-wrap">
          <Link to="/about/research-desk" className="text-zinc-300 hover:text-yellow-400 transition">{art.author_name}</Link>
          <span className="text-xs text-zinc-500">· Reviewed by MediaCrypto Editorial</span>
          <span>•</span>
          <span>{new Date(art.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          {art.updated_at && art.updated_at !== art.created_at && (
            <>
              <span>•</span>
              <span className="text-[var(--text-tertiary)]">Updated {new Date(art.updated_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
            </>
          )}
          <span>•</span>
          <span>{art.views || 0} views</span>
          {art.content && (
            <>
              <span>•</span>
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {Math.ceil(art.content.trim().split(/\s+/).length / 200)} min read
              </span>
            </>
          )}
        </div>

        {art.excerpt && (
          <p className="mt-8 text-xl text-[var(--text-secondary)] leading-relaxed font-medium border-l-4 border-yellow-400 pl-6 italic">
            {art.excerpt}
          </p>
        )}

        <div className="mt-8 flex justify-center">
          <AdBanner adKey="dc8c659b65e8c76e1b0272f7555e95da" width={728} height={90} />
        </div>

        <div className="mt-10 space-y-5">
          {renderContent(art.content)}
        </div>

        {art.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-[var(--border-soft)] flex flex-wrap gap-2">
            {art.tags.map((t) => (
              <span key={t} className="px-3 py-1 bg-[var(--bg-elevated)] border border-[var(--border-soft)] text-xs font-mono uppercase text-[var(--text-tertiary)] hover:text-yellow-400 hover:border-yellow-400/30 transition cursor-pointer">
                {"#" + t}
              </span>
            ))}
          </div>
        )}

        {art.sources?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-[var(--border-soft)]">
            <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">Sources & References</h3>
            <ul className="space-y-1.5">
              {art.sources.map((s, i) => (
                <li key={i}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm text-yellow-400 hover:underline">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ShareButtons title={art.title} url={articleUrl} />

        <div className="mt-8">
          <Link to="/news" className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] hover:text-yellow-400 transition">
            ← Back to News
          </Link>
        </div>
      </article>

      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20">
          <h3 className="font-display font-bold text-2xl text-[var(--text-primary)] border-b border-[var(--border-soft)] pb-4 mb-8">
            <span className="text-yellow-400">/</span> Related Stories
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.id} to={"/news/" + (r.slug || r.id)} className="block bg-[var(--bg-panel)] border border-[var(--border-soft)] hover:border-yellow-400/40 transition p-5 group">
                {r.image_url && (
                  <img src={r.image_url} alt={r.title} className="w-full h-32 object-cover mb-4" />
                )}
                <h4 className="font-display font-bold text-lg text-[var(--text-primary)] group-hover:text-yellow-400 transition">{r.title}</h4>
                <p className="mt-2 text-sm text-[var(--text-secondary)] line-clamp-2">{r.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}